/**
 * Procedure instance detail (RFC procedure-instance, Phase 2).
 *
 * One clinical event: its context (department / location / kind / status),
 * its team roster with roles, and the recordings grouped under it — with
 * add/remove + attach/detach. Tenant_admin.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormatDateTime } from '../../util/displayTimeZone';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOffIcon from '@mui/icons-material/LinkOff';

import {
  activeAdminTenantId,
  canWriteTenant,
  useAuth,
} from '../../auth/AuthContext';
import * as instancesApi from '../../api/procedureInstances';
import {
  INSTANCE_KIND_LABELS,
  INSTANCE_KINDS,
  INSTANCE_STATUSES,
  INSTANCE_STATUS_LABELS,
  TEAM_ROLES,
  TEAM_ROLE_LABELS,
} from '../../api/procedureInstances';
import * as orgApi from '../../api/organizations';
import * as proceduresApi from '../../api/procedures';
import * as sessionsApi from '../../api/sessions';
import * as tenantsApi from '../../api/tenants';
import { extractErrorMessage } from '../../api/errors';
import { procedureColor } from '../../util/procedureColors';
import { useSortedRows } from '../../util/tableSort';
import { SortHeader } from '../../components/SortHeader';
import { SearchableSelect } from '../../components/SearchableSelect';

/** Compact stat for the instance header's "live case" strip — a bold number
 *  over a small caption. All values come from the detail payload we already
 *  hold (team + sessions), so nothing here is fabricated. */
function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function duration(startedAt: string, endedAt: string): string {
  const s = Math.max(
    0,
    Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000),
  );
  return s >= 60 ? `${(s / 60).toFixed(1)} min` : `${s} s`;
}

function durationSeconds(startedAt: string, endedAt: string): number {
  return Math.max(
    0,
    (new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000,
  );
}

type TeamRow = {
  m: instancesApi.TeamAssignment;
  member: string;
  role: string;
  linked: boolean;
};
type RecordingRow = {
  s: instancesApi.ProcedureInstanceDetail['sessions'][number];
  participant: string;
};

// `added` has no header: it is the roster's natural (added-first) order, so
// the table opens as it always has until a column is clicked.
const TEAM_ACCESSORS = {
  added: (r: TeamRow) => r.m.created_at,
  member: (r: TeamRow) => r.member,
  role: (r: TeamRow) => r.role,
  recording: (r: TeamRow) => r.linked,
};
const RECORDING_ACCESSORS = {
  procedure: (r: RecordingRow) => r.s.procedure_name,
  participant: (r: RecordingRow) => r.participant,
  started: (r: RecordingRow) => r.s.started_at,
  duration: (r: RecordingRow) =>
    r.s.started_at && r.s.ended_at
      ? durationSeconds(r.s.started_at, r.s.ended_at)
      : null,
};
const NO_TEAM: TeamRow[] = [];
const NO_RECORDINGS: RecordingRow[] = [];

const NONE = [{ value: '', label: '— none —' }];
const KEEP_CURRENT = [{ value: '', label: '— keep current —' }];
const UNTRACKED = [{ value: '', label: '— untracked —' }];
const KIND_OPTIONS = INSTANCE_KINDS.map((k) => ({
  value: k as string,
  label: INSTANCE_KIND_LABELS[k],
}));
const STATUS_OPTIONS = INSTANCE_STATUSES.map((st) => ({
  value: st as string,
  label: INSTANCE_STATUS_LABELS[st],
}));
const ROLE_OPTIONS = TEAM_ROLES.map((r) => ({
  value: r as string,
  label: TEAM_ROLE_LABELS[r],
}));

export function TenantProcedureInstanceDetail() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fmtDateTime = useFormatDateTime();
  const tenantId = activeAdminTenantId(user);
  const canWrite = canWriteTenant(user, tenantId);

  const [detail, setDetail] =
    useState<instancesApi.ProcedureInstanceDetail | null>(null);
  const [departments, setDepartments] = useState<orgApi.Department[]>([]);
  const [locations, setLocations] = useState<orgApi.Location[]>([]);
  const [participants, setParticipants] = useState<tenantsApi.Participant[]>([]);
  const [procedures, setProcedures] = useState<proceduresApi.Procedure[]>([]);
  const [ungrouped, setUngrouped] = useState<sessionsApi.CaptureSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!tenantId || !id) return;
    setError(null);
    try {
      const [d, depts, locs, parts, sess, procs] = await Promise.all([
        instancesApi.getInstance(tenantId, id),
        orgApi.listDepartments(tenantId),
        orgApi.listLocations(tenantId),
        tenantsApi.listParticipants(tenantId),
        sessionsApi.listCaptureSessions(tenantId, false),
        proceduresApi.listProcedures(tenantId),
      ]);
      setDetail(d);
      setDepartments(depts);
      setLocations(locs);
      setParticipants(parts);
      setProcedures(procs);
      setUngrouped(sess.filter((s) => s.procedure_instance_id == null));
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load instance'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const deptName = (d: string | null) =>
    d ? (departments.find((x) => x.id === d)?.name ?? '—') : '—';
  const locName = (l: string | null) =>
    l ? (locations.find((x) => x.id === l)?.name ?? '—') : '—';
  const partName = (uid: string | null) => {
    const p = uid ? participants.find((x) => x.id === uid) : undefined;
    return p ? (p.full_name || p.email || 'participant') : null;
  };

  // ---- Edit dialog ----
  const [editOpen, setEditOpen] = useState(false);
  const [eProcId, setEProcId] = useState('');
  const [eKind, setEKind] = useState('');
  const [eStatus, setEStatus] = useState('scheduled');
  const [eDept, setEDept] = useState('');
  const [eLoc, setELoc] = useState('');
  const [eRef, setERef] = useState('');
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    if (!detail) return;
    setEProcId(detail.procedure_id ?? '');
    setEKind(detail.kind ?? '');
    setEStatus(detail.status);
    setEDept(detail.department_id ?? '');
    setELoc(detail.location_id ?? '');
    setERef(detail.external_case_ref ?? '');
    setEditOpen(true);
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId || !id) return;
    setSaving(true);
    setError(null);
    try {
      const proc = procedures.find((p) => p.id === eProcId);
      await instancesApi.updateInstance(tenantId, id, {
        // Only change the procedure when one is picked; leave the current
        // name untouched otherwise (a legacy instance may have no id).
        ...(proc
          ? { procedure_id: proc.id, procedure_name: proc.name }
          : {}),
        kind: eKind || null,
        status: eStatus,
        department_id: eDept || null,
        location_id: eLoc || null,
        external_case_ref: eRef.trim() || null,
      });
      setEditOpen(false);
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to update instance'));
    } finally {
      setSaving(false);
    }
  };

  // ---- Add member dialog ----
  const [memberOpen, setMemberOpen] = useState(false);
  const [mRole, setMRole] = useState('lead_surgeon');
  const [mParticipant, setMParticipant] = useState('');
  const [mLabel, setMLabel] = useState('');
  const [mSaving, setMSaving] = useState(false);

  const submitMember = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId || !id) return;
    setMSaving(true);
    setError(null);
    try {
      await instancesApi.addTeamMember(tenantId, id, {
        role: mRole,
        participant_user_id: mParticipant || null,
        participant_label:
          !mParticipant && mLabel.trim() ? mLabel.trim() : null,
      });
      setMemberOpen(false);
      setMParticipant('');
      setMLabel('');
      setMRole('lead_surgeon');
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to add member'));
    } finally {
      setMSaving(false);
    }
  };

  const act = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'action failed'));
    }
  };

  // ---- Attach recording dialog ----
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachId, setAttachId] = useState('');
  const [showAll, setShowAll] = useState(false);

  // Smart match: score each ungrouped recording against this instance so
  // the likely ones surface first. Signals — same procedure, a recording
  // by a team member, and a start time near the instance's window. A
  // free-text procedure_name match is a soft fallback since the capture
  // app's name is a snapshot, not the linked id.
  const teamUserIds = useMemo(
    () =>
      new Set(
        (detail?.team ?? [])
          .map((t) => t.participant_user_id)
          .filter((x): x is string => !!x),
      ),
    [detail],
  );
  const timeRef =
    detail?.started_at ?? detail?.scheduled_at ?? detail?.ended_at ?? null;
  const scored = useMemo(() => {
    const score = (s: sessionsApi.CaptureSession) => {
      let n = 0;
      const reasons: string[] = [];
      if (
        detail?.procedure_id &&
        s.procedure_id &&
        s.procedure_id === detail.procedure_id
      ) {
        n += 2;
        reasons.push('procedure');
      } else if (
        detail?.procedure_name &&
        s.procedure_name &&
        s.procedure_name.toLowerCase() === detail.procedure_name.toLowerCase()
      ) {
        n += 2;
        reasons.push('procedure');
      }
      if (s.participant_user_id && teamUserIds.has(s.participant_user_id)) {
        n += 2;
        reasons.push('team');
      }
      if (timeRef && s.started_at) {
        const hrs =
          Math.abs(
            new Date(s.started_at).getTime() - new Date(timeRef).getTime(),
          ) / 3.6e6;
        if (hrs <= 12) {
          n += 1;
          reasons.push('time');
        }
      }
      return { s, n, reasons };
    };
    return ungrouped
      .map(score)
      .sort(
        (a, b) =>
          b.n - a.n ||
          new Date(b.s.started_at).getTime() -
            new Date(a.s.started_at).getTime(),
      );
  }, [ungrouped, detail, teamUserIds, timeRef]);

  const likely = scored.filter((x) => x.n > 0);
  const attachRows = showAll || likely.length === 0 ? scored : likely;

  // Sortable Team + Recordings tables. Names resolve through the participant
  // roster, so derive them once here and sort on the resolved text.
  const partById = useMemo(
    () => new Map(participants.map((p) => [p.id, p])),
    [participants],
  );
  const teamRows = useMemo<TeamRow[]>(() => {
    if (!detail) return NO_TEAM;
    return detail.team.map((m) => {
      const p = m.participant_user_id
        ? partById.get(m.participant_user_id)
        : undefined;
      return {
        m,
        member:
          (p ? p.full_name || p.email || 'participant' : null) ??
          m.participant_label ??
          '',
        role: TEAM_ROLE_LABELS[m.role as instancesApi.TeamRole] ?? m.role,
        linked: !!m.session_id,
      };
    });
  }, [detail, partById]);
  const recordingRows = useMemo<RecordingRow[]>(() => {
    if (!detail) return NO_RECORDINGS;
    return detail.sessions.map((sess) => {
      const p = sess.participant_user_id
        ? partById.get(sess.participant_user_id)
        : undefined;
      return {
        s: sess,
        participant:
          (p ? p.full_name || p.email || 'participant' : null) ??
          sess.participant_label ??
          '',
      };
    });
  }, [detail, partById]);
  const { sort: teamSort, sorted: sortedTeam } = useSortedRows(
    teamRows,
    TEAM_ACCESSORS,
    'added',
  );
  const { sort: recSort, sorted: sortedRecordings } = useSortedRows(
    recordingRows,
    RECORDING_ACCESSORS,
    'started',
  );

  if (!tenantId) return <Alert severity="warning">No tenant selected.</Alert>;
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!detail) {
    return (
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tenant/procedure-instances')}
        >
          Back to instances
        </Button>
      </Stack>
    );
  }

  // "Live case" summary — computed from the detail payload we already hold:
  // team size, distinct devices assigned to the team, recordings grouped
  // under the instance, and the role breakdown.
  const deviceCount = new Set(
    detail.team.map((t) => t.device_id).filter(Boolean),
  ).size;
  const roleBreakdown = (() => {
    const m = new Map<string, number>();
    for (const t of detail.team) {
      const role = t.role || 'member';
      m.set(role, (m.get(role) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  })();

  return (
    <Stack spacing={3}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/tenant/procedure-instances')}
        sx={{ alignSelf: 'flex-start' }}
      >
        All instances
      </Button>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Header */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: procedureColor(detail.procedure_name),
                }}
              />
              <Typography variant="h5">{detail.procedure_name}</Typography>
              <Chip
                size="small"
                label={
                  INSTANCE_STATUS_LABELS[
                    detail.status as instancesApi.InstanceStatus
                  ] ?? detail.status
                }
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {deptName(detail.department_id)} · {locName(detail.location_id)}
              {detail.kind
                ? ` · ${INSTANCE_KIND_LABELS[detail.kind] ?? detail.kind}`
                : ''}
              {detail.external_case_ref
                ? ` · case ${detail.external_case_ref}`
                : ''}
            </Typography>
            {/* Live-case summary from the data we already hold. */}
            <Stack
              direction="row"
              spacing={3}
              sx={{ mt: 1.75, flexWrap: 'wrap', rowGap: 1.5 }}
            >
              <Stat label="Team" value={detail.team.length} />
              <Stat label="Devices" value={deviceCount} />
              <Stat label="Recordings" value={detail.sessions.length} />
            </Stack>
            {roleBreakdown.length > 0 && (
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}
              >
                {roleBreakdown.map(([role, count]) => (
                  <Chip
                    key={role}
                    size="small"
                    variant="outlined"
                    label={`${count} ${
                      TEAM_ROLE_LABELS[role as keyof typeof TEAM_ROLE_LABELS] ??
                      role.replace(/_/g, ' ')
                    }`}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            {canWrite && (
              <Button size="small" onClick={openEdit}>
                Edit
              </Button>
            )}
            {canWrite && (
              <Button
                size="small"
                color="error"
                onClick={() =>
                  act(async () => {
                    await instancesApi.deleteInstance(tenantId, detail.id);
                    navigate('/tenant/procedure-instances');
                  })
                }
              >
                Delete
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Team */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Team ({detail.team.length})
          </Typography>
          {canWrite && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setMemberOpen(true)}
            >
              Add member
            </Button>
          )}
        </Stack>
        {detail.team.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No team members yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortHeader label="Member" col="member" sort={teamSort} />
                <SortHeader label="Role" col="role" sort={teamSort} />
                <SortHeader label="Recording" col="recording" sort={teamSort} />
                {canWrite && <TableCell align="right" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTeam.map(({ m, member, role }) => (
                <TableRow key={m.id}>
                  <TableCell>{member || '—'}</TableCell>
                  <TableCell>{role}</TableCell>
                  <TableCell>
                    {m.session_id ? (
                      <Chip size="small" label="linked" color="success" />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  {canWrite && (
                    <TableCell align="right">
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          aria-label={`Remove ${
                            partName(m.participant_user_id) ??
                            m.participant_label ??
                            'member'
                          }`}
                          onClick={() =>
                            act(() =>
                              instancesApi.removeTeamMember(
                                tenantId,
                                detail.id,
                                m.id,
                              ),
                            )
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Recordings */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Recordings ({detail.sessions.length})
          </Typography>
          {canWrite && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setAttachId('');
                setShowAll(false);
                setAttachOpen(true);
              }}
              disabled={ungrouped.length === 0}
            >
              Attach recording
            </Button>
          )}
        </Stack>
        {detail.sessions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No recordings grouped yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortHeader label="Procedure" col="procedure" sort={recSort} />
                <SortHeader label="Participant" col="participant" sort={recSort} />
                <SortHeader label="Started" col="started" sort={recSort} />
                <SortHeader label="Duration" col="duration" sort={recSort} />
                {canWrite && <TableCell align="right" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRecordings.map(({ s, participant }) => (
                <TableRow key={s.id}>
                  <TableCell>{s.procedure_name}</TableCell>
                  <TableCell>{participant || '—'}</TableCell>
                  <TableCell>
                    {fmtDateTime(s.started_at)}
                  </TableCell>
                  <TableCell>{duration(s.started_at, s.ended_at)}</TableCell>
                  {canWrite && (
                    <TableCell align="right">
                      <Tooltip title="Detach">
                        <IconButton
                          size="small"
                          aria-label={`Detach ${s.procedure_name}`}
                          onClick={() =>
                            act(() =>
                              instancesApi.detachSession(
                                tenantId,
                                detail.id,
                                s.id,
                              ),
                            )
                          }
                        >
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <form onSubmit={saveEdit}>
          <DialogTitle>Edit instance</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <SearchableSelect
                label="Procedure"
                fullWidth
                size="medium"
                value={eProcId}
                onChange={setEProcId}
                disabled={saving}
                helperText={
                  eProcId
                    ? undefined
                    : `Current: ${detail.procedure_name}. Pick to change.`
                }
                pinned={KEEP_CURRENT}
                options={procedures.map((p) => ({
                  value: p.id,
                  label: `${p.name} · #${p.procedure_number}`,
                }))}
              />
              <Stack direction="row" spacing={2}>
                <SearchableSelect
                  label="Kind"
                  fullWidth
                  size="medium"
                  value={eKind}
                  onChange={setEKind}
                  disabled={saving}
                  pinned={NONE}
                  options={KIND_OPTIONS}
                />
                {/* Lifecycle order (scheduled → completed …), not alphabetical. */}
                <SearchableSelect
                  keepOrder
                  label="Status"
                  fullWidth
                  size="medium"
                  value={eStatus}
                  onChange={setEStatus}
                  disabled={saving}
                  options={STATUS_OPTIONS}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <SearchableSelect
                  label="Department"
                  fullWidth
                  size="medium"
                  value={eDept}
                  onChange={setEDept}
                  disabled={saving}
                  pinned={NONE}
                  options={departments.map((d) => ({ value: d.id, label: d.name }))}
                />
                <SearchableSelect
                  label="Location"
                  fullWidth
                  size="medium"
                  value={eLoc}
                  onChange={setELoc}
                  disabled={saving}
                  pinned={NONE}
                  options={locations.map((l) => ({ value: l.id, label: l.name }))}
                />
              </Stack>
              <TextField
                label="External case reference"
                fullWidth
                value={eRef}
                onChange={(e) => setERef(e.target.value)}
                disabled={saving}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Add member dialog */}
      <Dialog open={memberOpen} onClose={() => setMemberOpen(false)} fullWidth maxWidth="xs">
        <form onSubmit={submitMember}>
          <DialogTitle>Add team member</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <SearchableSelect
                label="Role"
                fullWidth
                size="medium"
                value={mRole}
                onChange={setMRole}
                disabled={mSaving}
                options={ROLE_OPTIONS}
              />
              <SearchableSelect
                label="Participant"
                fullWidth
                size="medium"
                value={mParticipant}
                onChange={setMParticipant}
                disabled={mSaving}
                helperText="A tracked participant, or leave blank and name an untracked member below."
                pinned={UNTRACKED}
                options={participants.map((p) => ({
                  value: p.id,
                  label: p.full_name || p.email,
                }))}
              />
              {!mParticipant && (
                <TextField
                  label="Name / label (untracked)"
                  fullWidth
                  value={mLabel}
                  onChange={(e) => setMLabel(e.target.value)}
                  disabled={mSaving}
                  placeholder="e.g. Scrub Nurse"
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMemberOpen(false)} disabled={mSaving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={mSaving}>
              {mSaving ? 'Adding…' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Attach recording dialog */}
      <Dialog open={attachOpen} onClose={() => setAttachOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Attach a recording</DialogTitle>
        <DialogContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              {showAll || likely.length === 0
                ? 'All ungrouped recordings'
                : `Likely matches (${likely.length}) — same procedure, team, or time`}
            </Typography>
            {likely.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showAll}
                    onChange={(e) => setShowAll(e.target.checked)}
                  />
                }
                label="Show all"
              />
            )}
          </Stack>
          {attachRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No ungrouped recordings to attach.
            </Typography>
          ) : (
            // keepOrder: ranked best-match first (then newest), which is
            // the point of this picker — alphabetical would bury the likely one.
            <SearchableSelect
              keepOrder
              label="Ungrouped recording"
              fullWidth
              size="medium"
              value={attachId}
              onChange={setAttachId}
              sx={{ mt: 1 }}
              options={attachRows.map(({ s, reasons }) => ({
                value: s.id,
                label: `${s.procedure_name} · ${
                  partName(s.participant_user_id) ?? s.participant_label ?? '—'
                } · ${fmtDateTime(s.started_at)}${
                  reasons.length ? ` — matches ${reasons.join(', ')}` : ''
                }`,
              }))}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!attachId}
            onClick={() =>
              act(async () => {
                await instancesApi.attachSession(tenantId, detail.id, attachId);
                setAttachOpen(false);
              })
            }
          >
            Attach
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
